/* eslint-disable react/no-unknown-property */
import React, { memo, useMemo, useRef, useEffect, useCallback } from 'react';
import { GridStack } from 'gridstack';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { isEmptyObject } from 'utils/objects';

import 'gridstack/dist/gridstack.min.css';

import styles from './styles';

const useStyles = createUseStyles(styles, 'GridMotion');

const BASE_LAYOUT_SPECS = {
    w: 1,
    h: 7,
    minW: 1, // minimum number of columns the widget has as width
    minH: 7, // minimum number of rows the widget has as height
    moded: false,
    static: false,
};

const Grid = memo(
    ({
        widgets,
        onLayoutChange,
        selected,
        baseLayoutSpecs = BASE_LAYOUT_SPECS,
        onChangeGridWidgets,
        initGrid,
        initWidget,
        widgetComp,
        getWidgetComp,
        isLazy,
        canDrag = true,
        canResize = true,
        gridOptions,
        useBreakpoints = false,
        columnMax = 4,
        columnNum = 4,
        ...props
    }) => {
        const classes = useStyles();
        const gridRef = useRef(null);
        const gridInstance = useRef(null);
        const elementsRef = useRef({});
        const bufferElementsRef = useRef(null);
        const isFirstRender = useRef(true);
        const canEditGrid = true;
        const innerWidgets = useRef(widgets || []);

        // Lazy Refs
        const containerRef = useRef(null);
        const widgetsRef = useRef({});
        const loadedVisuals = useRef([]);

        const dashboardScrollToBottom = useCallback(() => {
            if (containerRef.current) {
                containerRef.current.scrollTop = containerRef.current.scrollHeight;
            }
        }, []);

        const checkElementVisibility = useCallback(({ element }) => {
            const rect = element.getBoundingClientRect();
            const isFullyVisible =
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth);

            const elementVisibleHeight =
                Math.min(rect.bottom, window.innerHeight || document.documentElement.clientHeight) -
                Math.max(rect.top, 0);

            const isPartiallyVisible = elementVisibleHeight > 0;

            return isFullyVisible || isPartiallyVisible || false;
        }, []);

        const initWidgets = useCallback(
            (props) => {
                if (!initWidget) return;
                if (!isEmptyObject(elementsRef?.current)) {
                    const visibleWidgets = Object.entries(elementsRef.current).reduce(
                        (arr, [key, ref]) => {
                            if (ref && checkElementVisibility({ element: ref })) {
                                arr.push(key);
                            }
                            return arr;
                        },
                    );
                    visibleWidgets.forEach((id) => {
                        if (!loadedVisuals.current.includes(id)) {
                            loadedVisuals.current.push(id);
                            initWidget({ id, ...props });
                        }
                    });
                }
            },
            [checkElementVisibility, initWidget],
        );

        const initHiddenWidgets = useCallback(
            (props) => {
                return new Promise((resolve, reject) => {
                    if (!initWidget || isEmptyObject(elementsRef?.current)) return resolve();

                    let promises = [];
                    const hiddenWidgets = Object.entries(elementsRef.current).reduce(
                        (arr, [key, ref]) => {
                            if (ref && !checkElementVisibility({ element: ref })) {
                                arr.push(key);
                            }
                            return arr;
                        },
                    );

                    hiddenWidgets.forEach((id) => {
                        if (!loadedVisuals.current.includes(id)) {
                            loadedVisuals.current.push(id);
                            promises.push(initWidget({ id, ...props }));
                        }
                    });
                    return Promise.all(promises).then((results) => {
                        resolve(results);
                    });
                });
            },
            [checkElementVisibility, initWidget],
        );

        const resetWidgets = useCallback(
            (props) => {
                loadedVisuals.current = [];
                initWidgets(props);
            },
            [initWidgets],
        );

        const findEmptySpot = useCallback(({ w, h }) => {
            const gridWidth = gridInstance.current.engine.column;
            const maxHeight = 1000;

            for (let y = 0; y < maxHeight; y++) {
                for (let x = 0; x < gridWidth; x++) {
                    if (gridInstance.current.isAreaEmpty(x, y, w, h)) {
                        return { x, y };
                    }
                }
            }

            return null;
        }, []);

        const getLastRow = useCallback(() => {
            return gridInstance.current.engine.getRow();
        }, []);

        const onDragStart = useCallback((event, el) => {
            if (gridInstance.current) {
                // When dragstart we set maxRow, to prevent infinite scroll. So the grid allows to move
                // any widget to the bottom, but only adding the height of the moved widget.
                const widgetHeight = parseInt(el.getAttribute('gs-h'));
                const currentGridHeight = gridInstance.current.engine.getRow();
                gridInstance.current.engine.maxRow = currentGridHeight + widgetHeight;
            }
        }, []);

        const onDragStop = useCallback((event, el) => {
            if (gridInstance.current) {
                gridInstance.current.engine.maxRow = null;
            }
        }, []);

        const onGridChange = useCallback(
            (event, items) => {
                // Gridstack eventListeners need to avoid rerenderings. We should avoid dependencies
                // and use ref instead. Otherwise we will have undesired behaviours.
                // This applies also to methods. The methods used within the eventListeners of
                // Gridstack should not have dependencies.
                const modifiedWidgets = items.reduce((arr, item) => {
                    const widget = innerWidgets.current.filter(
                        (widget) => widget.id.toString() === item.id.toString(),
                    )[0];

                    if (!widget) return arr;

                    const newPosition = { w: item.w, h: item.h, x: item.x, y: item.y };
                    const newWidget = {
                        ...widget,
                        position: {
                            ...(widget?.position || {}),
                            ...newPosition,
                        },
                    };
                    arr.push(newWidget);
                    return arr;
                }, []);

                if (!modifiedWidgets?.length) return;

                const mappedModifiedWidgets = modifiedWidgets.reduce((obj, current) => {
                    obj[current.id] = current;
                    return obj;
                }, {});

                const allWidgets = innerWidgets.current.map((current) => {
                    if (mappedModifiedWidgets[current.id]) return mappedModifiedWidgets[current.id];
                    return current;
                });

                onChangeGridWidgets({ modifiedWidgets, widgets: allWidgets });
            },
            [onChangeGridWidgets],
        );

        useEffect(() => {
            if (gridRef.current && !gridInstance.current) {
                let options = {
                    cellHeight: '60px',
                    margin: '8px',
                    verticalMargin: '8px',
                    disableOneColumnMode: true,
                    disableDrag: !canDrag,
                    disableResize: !canResize,
                    float: false,
                    draggable: {
                        scroll: true,
                        appendTo: 'body',
                    },
                    staticGrid: !canEditGrid,
                    ...gridOptions,
                };

                if (useBreakpoints) {
                    options.columnOpts = {
                        columnMax: columnMax,
                        breakpoints: [
                            { w: 1750, c: 4 },
                            { w: 1500, c: 3 },
                            { w: 992, c: 2 },
                            { w: 768, c: 1 },
                        ],
                    };
                } else {
                    options.column = columnNum;
                }

                if (gridOptions) options = { ...options, ...gridOptions };

                gridInstance.current = GridStack.init(options, gridRef.current);
                // Handle update of layout after resize or drag and drop
                // The eventListeners have to be always in the same useEffect as the GridStack.init,
                // otherwise we will experience side effects due to the asynchrony of execution
                gridInstance.current.on('dragstart', onDragStart);
                gridInstance.current.on('dragstop', onDragStop);
                gridInstance.current.on('change', onGridChange);

                initGrid &&
                    initGrid({
                        grid: gridInstance.current,
                        findEmptySpot,
                        getLastRow,
                        resetWidgets,
                        initHiddenWidgets,
                    });
            }

            return () => {
                if (gridInstance.current) {
                    // We need to remove the eventlisteners and distroy and reset the gridInstance.
                    // If we don't reset the gridInstance, this results in problems with the hotreload.
                    gridInstance.current.off('change', onGridChange);
                    gridInstance.current.off('dragstart', onDragStart);
                    gridInstance.current.off('dragstop', onDragStop);
                    gridInstance.current.destroy(false);
                    gridInstance.current = null;
                }
            };
        }, [
            canEditGrid,
            initGrid,
            findEmptySpot,
            getLastRow,
            resetWidgets,
            onDragStart,
            canDrag,
            canResize,
            gridOptions,
            useBreakpoints,
            onDragStop,
            onGridChange,
            initHiddenWidgets,
            columnMax,
            columnNum,
        ]);

        useEffect(() => {
            if (gridInstance.current) {
                if (bufferElementsRef.current === null) {
                    // Initialize bufferElementsRef the first time
                    // The bufferElementsRef, allow us to keep the reference of elements that doesn't exist
                    // anymore in the grid, but that we need to inform Gridstack that have to be deleted
                    // from its internal control
                    // That way, we can add or delete the elements in bufferElementsRef exactly when we need it
                    bufferElementsRef.current = { ...(elementsRef.current || {}) };
                }

                const addedWidgets = widgets.filter(
                    (widget) =>
                        !innerWidgets.current.some((prevWidget) => prevWidget.id === widget.id),
                );

                const removedWidgets = innerWidgets.current.filter(
                    (prevWidget) => !widgets.some((widget) => widget.id === prevWidget.id),
                );

                innerWidgets.current = widgets || [];

                if (addedWidgets.length) {
                    addedWidgets.forEach((widget) => {
                        const element = elementsRef.current[widget.id];
                        // Let know Gridstack what are the added widgets for a good GridStack handling of widgets.
                        if (element) {
                            bufferElementsRef.current[widget.id] = element;
                        }
                    });
                    if (isLazy) initWidgets();
                    dashboardScrollToBottom();
                }

                if (removedWidgets.length) {
                    removedWidgets.forEach((widget) => {
                        // This spread is important to create a new instance of the widget.
                        // So the deletion of widgets happening below doesn't falls in conflict with removeWidget
                        // handled by the grid an creating an undesired UX bad behavior.
                        const element = { ...(bufferElementsRef.current?.[widget.id] || {}) };
                        // Let know Gridstack what are the widget that no longer exist.
                        // So it doesn't keep the space that was occupied by it.
                        if (!isEmptyObject(element)) {
                            gridInstance.current.removeWidget(element, false, false);
                            delete elementsRef.current[widget.id];
                            delete bufferElementsRef.current[widget.id];
                        }
                    });
                }
            }
        }, [widgets, dashboardScrollToBottom, isLazy, initWidgets]);

        const throttle = useCallback((func, limit) => {
            let inThrottle;
            return function () {
                const args = arguments;
                const context = this;
                if (!inThrottle) {
                    func.apply(context, args);
                    inThrottle = true;
                    setTimeout(() => (inThrottle = false), limit);
                }
            };
        }, []);

        useEffect(() => {
            const element = containerRef.current;

            const handleScroll = throttle(() => initWidgets(), 100);
            const handleResize = throttle(() => initWidgets(), 100);

            if (isLazy) {
                if (element) element.addEventListener('scroll', handleScroll);
                window.addEventListener('resize', handleResize);
            }

            return () => {
                if (isLazy) {
                    if (element) element.removeEventListener('scroll', handleScroll);
                    window.removeEventListener('resize', handleResize);
                }
            };
        }, [throttle, initWidgets, isLazy]);

        useEffect(() => {
            if (isFirstRender.current && isLazy) {
                isFirstRender.current = false;
                // Timeout waiting all the widgetRefs are available to check what widgets
                // are visible and have to be loaded
                setTimeout(() => {
                    initWidgets();
                }, 100);
            }
        }, [initWidgets, isLazy]);

        const renderWidgets = useMemo(() => {
            if (!widgets?.length) return null;

            const Widget = widgetComp || null;

            // Sort widgets by position (y first, then x) to ensure proper DOM order
            // This helps GridStack respect gs-x and gs-y positioning
            const sortedWidgets = [...widgets].sort((a, b) => {
                const posA = a.position || {};
                const posB = b.position || {};

                // Sort by y first, then by x
                if (posA.y !== posB.y) {
                    return (posA.y || 0) - (posB.y || 0);
                }
                return (posA.x || 0) - (posB.x || 0);
            });

            return sortedWidgets.map((current) => {
                const isSelected = selected.includes(current.id);
                const position = current.position;

                return (
                    <div
                        ref={(ref) => {
                            elementsRef.current[current.id] = ref;
                        }}
                        key={current.id.toString()}
                        className="grid-stack-item"
                        data-id={current.id.toString()}
                        gs-id={current.id.toString()}
                        gs-w={position.w}
                        gs-h={position.h}
                        gs-x={position.x}
                        gs-y={position.y}
                        gs-min-w={baseLayoutSpecs?.minW ? baseLayoutSpecs.minW : position.minW}
                        gs-min-h={baseLayoutSpecs?.minH ? baseLayoutSpecs?.minH : position.minH}
                        gs-locked={current.static ? 'true' : 'false'}
                        gs-no-resize={current.static ? 'true' : 'false'}
                        gs-no-move={current.static ? 'true' : 'false'}
                    >
                        <div
                            className={classnames('grid-stack-item-content', classes.gridItem, {
                                [classes.disabledDrag]: !canDrag,
                            })}
                        >
                            {getWidgetComp &&
                                !widgetComp &&
                                getWidgetComp({ data: current, isSelected, ...props })}
                            {!getWidgetComp && widgetComp && (
                                <Widget
                                    getRef={(ref) => {
                                        widgetsRef.current[current.id] = ref;
                                    }}
                                    data={current}
                                    isSelected={isSelected}
                                    initWidget={!isLazy && initWidget}
                                    {...props}
                                />
                            )}
                        </div>
                    </div>
                );
            });
        }, [
            widgets,
            widgetComp,
            selected,
            baseLayoutSpecs.minW,
            baseLayoutSpecs?.minH,
            classes.gridItem,
            classes.disabledDrag,
            canDrag,
            getWidgetComp,
            props,
            isLazy,
            initWidget,
        ]);

        if (!widgets.length) return null;

        return (
            <div ref={containerRef} className={classes.root}>
                <div ref={gridRef} className="grid-stack">
                    {renderWidgets}
                </div>
            </div>
        );
    },
);

export default Grid;
